import { Offer } from "@/services/backend/qpm/offers/types/offer";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import t from "@/lang/lang";
import { FloatNumberInput } from "@/shared/components/domain/qpm/inputs/number-input";
import {
  resolvedPriceAvailability,
  resolvedPriceVisibility,
} from "@/routes/processes/[processId]/qpm/offers/[offerId]/_components/price-visibility";
import { InfoIconTooltip } from "@/shared/components/domain/qpm/info-icon-tooltip";

export type InstallationTableComponentProps = {
  offer: Offer;
};

export function InstallationTableComponent({
  offer,
}: InstallationTableComponentProps) {
  const disabled = offer.immutable;
  const visibility = resolvedPriceVisibility(offer);
  const availability = resolvedPriceAvailability(offer);
  const hiddenBySettingText = t(
    "Diese Position ist im PDF ausgeblendet, da sie in den Preispositionen deaktiviert wurde.",
  );
  return (
    <Table className="min-w-[560px]">
      <TableHeader>
        <TableRow className="bg-muted/30">
          <TableHead className="h-10 px-2 py-2 font-semibold">
            {t("Position")}
          </TableHead>
          <TableHead className="h-10 px-2 py-2 text-end font-semibold">
            {t("Gesamtpreis")}
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {availability.material.qpm && (
          <TableRow
            className={
              visibility.material.qpm ? "border-none" : "border-none opacity-50"
            }
          >
            <TableCell className="px-2 py-2.5 text-sm font-medium sm:text-base">
              <div className="flex items-center gap-2">
                <span>{t("Anschaffung QPM Boxen- und Fallensystem")}</span>
                {!visibility.material.qpm ? (
                  <InfoIconTooltip text={hiddenBySettingText} />
                ) : null}
              </div>
            </TableCell>
            <TableCell className="px-2 py-2.5 text-end">
              <FloatNumberInput
                className="ml-auto w-32"
                value={offer.materialCost.qpm.totalCost}
                disabled={disabled || !visibility.material.qpm}
              />
            </TableCell>
          </TableRow>
        )}
        {availability.material.fiv && (
          <TableRow
            className={
              visibility.material.fiv ? "border-none" : "border-none opacity-50"
            }
          >
            <TableCell className="px-2 py-2.5 text-sm font-medium sm:text-base">
              <div className="flex items-center gap-2">
                <span>{t("Anschaffung Fluginsektenvernichter")}</span>
                {!visibility.material.fiv ? (
                  <InfoIconTooltip text={hiddenBySettingText} />
                ) : null}
              </div>
            </TableCell>
            <TableCell className="px-2 py-2.5 text-end">
              <FloatNumberInput
                className="ml-auto w-32"
                value={offer.materialCost.fiv.totalCost}
                disabled={disabled || !visibility.material.fiv}
              />
            </TableCell>
          </TableRow>
        )}
        {availability.material.additions && (
          <TableRow
            className={
              visibility.material.additions
                ? "border-none"
                : "border-none opacity-50"
            }
          >
            <TableCell className="px-2 py-2.5 text-sm font-medium sm:text-base">
              <div className="flex items-center gap-2">
                <span>{t("Zusatzmaterial")}</span>
                {!visibility.material.additions ? (
                  <InfoIconTooltip text={hiddenBySettingText} />
                ) : null}
              </div>
            </TableCell>
            <TableCell className="px-2 py-2.5 text-end">
              <FloatNumberInput
                className="ml-auto w-32"
                value={offer.materialCost.additions.totalCost}
                disabled={disabled || !visibility.material.additions}
              />
            </TableCell>
          </TableRow>
        )}
        {availability.installation.qpm && (
          <TableRow
            className={
              visibility.installation.qpm
                ? "border-none"
                : "border-none opacity-50"
            }
          >
            <TableCell className="px-2 py-2.5 text-sm font-medium sm:text-base">
              <div className="flex items-center gap-2">
                <span>{t("Installation QPM")}</span>
                {!visibility.installation.qpm ? (
                  <InfoIconTooltip text={hiddenBySettingText} />
                ) : null}
              </div>
            </TableCell>
            <TableCell className="px-2 py-2.5 text-end">
              <FloatNumberInput
                className="ml-auto w-32"
                value={offer.installationResult.qpm.totalCost}
                disabled={disabled || !visibility.installation.qpm}
              />
            </TableCell>
          </TableRow>
        )}
        {availability.installation.fiv && (
          <TableRow
            className={
              visibility.installation.fiv
                ? "border-none"
                : "border-none opacity-50"
            }
          >
            <TableCell className="px-2 py-2.5 text-sm font-medium sm:text-base">
              <div className="flex items-center gap-2">
                <span>{t("Installation FIV")}</span>
                {!visibility.installation.fiv ? (
                  <InfoIconTooltip text={hiddenBySettingText} />
                ) : null}
              </div>
            </TableCell>
            <TableCell className="px-2 py-2.5 text-end">
              <FloatNumberInput
                className="ml-auto w-32"
                value={offer.installationResult.fiv.totalCost}
                disabled={disabled || !visibility.installation.fiv}
              />
            </TableCell>
          </TableRow>
        )}
        {availability.installation.aLoRa && (
          <TableRow
            className={
              visibility.installation.aLoRa
                ? "border-none"
                : "border-none opacity-50"
            }
          >
            <TableCell className="px-2 py-2.5 text-sm font-medium sm:text-base">
              <div className="flex items-center gap-2">
                <span>{t("Installation ALoRa")}</span>
                {!visibility.installation.aLoRa ? (
                  <InfoIconTooltip text={hiddenBySettingText} />
                ) : null}
              </div>
            </TableCell>
            <TableCell className="px-2 py-2.5 text-end">
              <FloatNumberInput
                className="ml-auto w-32"
                value={offer.installationResult.aLoRa.totalCost}
                disabled={disabled || !visibility.installation.aLoRa}
              />
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}
